import { useEffect, useState, useMemo } from "react";
import {
  elapsedTimeContainer,
  formattedDate,
  formattedDuration,
  formattedPredictionDateTime,
} from "../../components/formatted/FormattedDateAndTime";
import { formattedStatus } from "../../components/formatted/FormattedText";
import {
  hasOpeningHours,
  calculateHistoryDays,
  extractReliablePrediction,
} from "../../shared/utils/utils";
import { truncateText } from "../../components/formatted/FormattedText";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AlertLightIndicator from "./AlertLightIndicator";
import MidCycleFluxChangeBadge from "./MidCycleFluxChangeBadge";
import { MIN_HISTORY_DAYS_FOR_PREDICTION } from "../../constants/cache";
import {
  CompactionIcon,
  FillingLevelIcon,
  WeightTonsIcon,
} from "../../ui/components/MonitoringMetricIcons";

const ContainerThumbnailView = ({
  data,
  weightKG,
  numberOfPackmat,
  currentDate,
  isLoadingHistoryCycleContainer,
  superContainer,
  fillingAlert,
}) => {
  const { t, i18n } = useTranslation();
  const containersData = useSelector((state) => state.containers.data);
  const [dataContainer, setDataContainer] = useState({});

  useEffect(() => {
    if (Object.keys(containersData).length !== 0) {
      if (data._id in containersData) {
        const containerActive = containersData[data._id];
        setDataContainer(containerActive);
      }
    }
  }, [containersData, data]);

  const fillingTimeOpacityClass = useMemo(() => {
    if (dataContainer?.durationFullFillingMs?.[0]?.duration === -1) {
      return "";
    }
    const reliablePredictionValue = extractReliablePrediction(dataContainer);
    const historyDays = calculateHistoryDays(
      dataContainer?.fromDate,
      currentDate,
    );
    const hasEnoughHistory =
      historyDays !== null && historyDays >= MIN_HISTORY_DAYS_FOR_PREDICTION;
    const shouldShowPrediction =
      reliablePredictionValue === true &&
      hasOpeningHours(data?.addressJoin?.opening) &&
      hasEnoughHistory &&
      dataContainer?.durationFullFillingMs?.[0]?.predictionDatetime;
    return shouldShowPrediction ? "" : "opacity-50";
  }, [dataContainer, data, currentDate]);

  return (
    <div className="w-full min-h-[265px] pr-6">
      <div className="flex flex-col gap-3">
        {/* Titre principal en cyan */}
        <div className="flex flex-col gap-2 min-w-0">
          <div className="flex flex-row gap-3 items-center flex-wrap">
            <span className="text-3xl font-semibold text-[#00A7A6]">
              {data.wasteName ? truncateText(data.wasteName, 25) : "Non défini"}
            </span>
            {data.activatedFillingLevelOption && data.collectorEmail && (
              <div className="flex flex-row gap-1 text-lg text-[#0F265C] items-center">
                <i className="fas fa-sync-alt"></i>
                <span>Auto</span>
              </div>
            )}
            <span className="flex flex-row gap-1 ">
              <AlertLightIndicator alertLight={data.alertLight} t={t} />
            </span>
          </div>
          <MidCycleFluxChangeBadge container={data} />
        </div>

        {/* Texte "mesuré il y a" juste en dessous */}
        {!superContainer && (
          <span className="text-xs text-gray-500 font-light">
            {(() => {
              const time = new Date(data._time).toLocaleTimeString(
                i18n.language === "fr"
                  ? "fr-FR"
                  : i18n.language === "es"
                    ? "es-ES"
                    : i18n.language === "se"
                      ? "sv-SE"
                      : "en-GB",
                { hour: "2-digit", minute: "2-digit" },
              );
              const formattedTimeString =
                i18n.language === "fr" ? time.replace(":", "h") : time;

              return (
                t("Taken") +
                " " +
                elapsedTimeContainer(
                  data._time,
                  i18n.language,
                  true,
                ).toLowerCase() +
                " - " +
                formattedDate(data._time, t) +
                " " +
                t("at") +
                " " +
                formattedTimeString
              );
            })()}
          </span>
        )}
      </div>

      {!superContainer && (
        <div className="mt-3 flex gap-2">
          <div
            className="bg-white border-[1.5px] border-black rounded-full"
            style={{
              paddingLeft: "0.8rem",
              paddingRight: "0.8rem",
              paddingTop: "0.275rem",
              paddingBottom: "0.275rem",
            }}
          >
            <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
              {truncateText(data.addressName, 20)}
              {data.positioning && data.positioning !== "" && (
                <>
                  {" "}
                  -{" "}
                  {truncateText(
                    data.positioning.charAt(0).toUpperCase() +
                      data.positioning.slice(1),
                    15,
                  )}
                </>
              )}
            </span>
          </div>

          <div
            className="bg-white border-[1.5px] border-black rounded-full"
            style={{
              paddingLeft: "0.8rem",
              paddingRight: "0.8rem",
              paddingTop: "0.275rem",
              paddingBottom: "0.275rem",
            }}
          >
            <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
              {formattedStatus(data.status)}
            </span>
          </div>
        </div>
      )}

      {/* Section métriques avec trait en haut */}
      <div className="flex flex-col gap-3 pt-3 mt-4 border-t-[1.2px] border-[#D3D3D3]">
        {/* Encadré de recommandation */}
        {fillingAlert &&
          (fillingAlert.rotationAlert ||
            (data.packmatActive &&
              (fillingAlert.packmatAlert ||
                fillingAlert.packmatPossibility))) && (
            <div
              style={{
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                padding: "10px 16px",
                background: "#E6ECFD",
                border: "1px solid #0143EC",
                borderRadius: "6px",
                gap: "12px",
                marginBottom: "8px",
              }}
            >
              <svg
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ flexShrink: 0, marginTop: "2px" }}
              >
                <path
                  d="M2.71096 15.5833C2.45123 15.5833 2.23353 15.4955 2.05783 15.3198C1.88214 15.1441 1.79429 14.9264 1.79429 14.6667C1.79429 14.4069 1.88214 14.1892 2.05783 14.0135C2.23353 13.8378 2.45123 13.75 2.71096 13.75H3.62762V7.33333C3.62762 6.06528 4.00957 4.93854 4.77346 3.95313C5.53735 2.96771 6.5304 2.32222 7.75262 2.01667V1.375C7.75262 0.993056 7.8863 0.668403 8.15367 0.401042C8.42103 0.133681 8.74568 0 9.12762 0C9.50957 0 9.83422 0.133681 10.1016 0.401042C10.3689 0.668403 10.5026 0.993056 10.5026 1.375V2.01667C11.7248 2.32222 12.7179 2.96771 13.4818 3.95313C14.2457 4.93854 14.6276 6.06528 14.6276 7.33333V13.75H15.5443C15.804 13.75 16.0217 13.8378 16.1974 14.0135C16.3731 14.1892 16.461 14.4069 16.461 14.6667C16.461 14.9264 16.3731 15.1441 16.1974 15.3198C16.0217 15.4955 15.804 15.5833 15.5443 15.5833H2.71096ZM9.12762 18.3333C8.62346 18.3333 8.19186 18.1538 7.83283 17.7948C7.4738 17.4358 7.29429 17.0042 7.29429 16.5H10.961C10.961 17.0042 10.7814 17.4358 10.4224 17.7948C10.0634 18.1538 9.63179 18.3333 9.12762 18.3333ZM5.46096 13.75H12.7943V7.33333C12.7943 6.325 12.4353 5.46181 11.7172 4.74375C10.9992 4.02569 10.136 3.66667 9.12762 3.66667C8.11929 3.66667 7.2561 4.02569 6.53804 4.74375C5.81998 5.46181 5.46096 6.325 5.46096 7.33333V13.75ZM0.877623 7.33333C0.617901 7.33333 0.400193 7.23403 0.224498 7.03542C0.048804 6.83681 -0.0237654 6.60764 0.00679012 6.34792C0.129012 5.20208 0.449846 4.13646 0.96929 3.15104C1.48873 2.16563 2.15332 1.30625 2.96304 0.572917C3.16165 0.404861 3.387 0.328472 3.63908 0.34375C3.89117 0.359028 4.0936 0.473611 4.24637 0.6875C4.39915 0.901389 4.46026 1.13056 4.42971 1.375C4.39915 1.61944 4.28457 1.83333 4.08596 2.01667C3.49012 2.58194 3.00123 3.23889 2.61929 3.9875C2.23735 4.73611 1.98526 5.54583 1.86304 6.41667C1.83248 6.67639 1.72554 6.8941 1.54221 7.06979C1.35887 7.24549 1.13735 7.33333 0.877623 7.33333ZM17.3776 7.33333C17.1179 7.33333 16.8964 7.24549 16.713 7.06979C16.5297 6.8941 16.4228 6.67639 16.3922 6.41667C16.27 5.54583 16.0179 4.73611 15.636 3.9875C15.254 3.23889 14.7651 2.58194 14.1693 2.01667C13.9707 1.83333 13.8561 1.61944 13.8255 1.375C13.795 1.13056 13.8561 0.901389 14.0089 0.6875C14.1617 0.473611 14.3641 0.359028 14.6162 0.34375C14.8682 0.328472 15.0936 0.404861 15.2922 0.572917C16.1019 1.30625 16.7665 2.16563 17.286 3.15104C17.8054 4.13646 18.1262 5.20208 18.2485 6.34792C18.279 6.60764 18.2064 6.83681 18.0308 7.03542C17.8551 7.23403 17.6373 7.33333 17.3776 7.33333Z"
                  fill="#0143EC"
                />
              </svg>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  flex: 1,
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#001B5E",
                  }}
                >
                  {t("Recommandation")}
                </span>
                <span style={{ fontSize: "14px", color: "#001B5E" }}>
                  {fillingAlert.rotationAlert
                    ? "Une rotation est à prévoir rapidement."
                    : data.packmatActive &&
                        (fillingAlert.packmatAlert ||
                          fillingAlert.packmatPossibility)
                      ? "Un compactage est à prévoir rapidement."
                      : ""}
                </span>
              </div>
            </div>
          )}

        {/* Taux de remplissage */}
        <div
          className={`flex items-center justify-between ${!data.activatedFillingLevelOption || data.filling_level === undefined ? "opacity-50" : ""}`}
        >
          <div className="flex items-center gap-2">
            <FillingLevelIcon />
            <span className="text-sm text-gray-700">{t("Filling level")}</span>
          </div>
          <span className="text-sm font-bold text-gray-900">
            {data.activatedFillingLevelOption &&
            data.filling_level !== undefined
              ? `${data.filling_level}%`
              : "..."}
          </span>
        </div>

        {/* Temps de remplissage */}
        <div
          className={`flex items-center justify-between ${fillingTimeOpacityClass}`}
        >
          <div className="flex items-center gap-2">
            <svg
              width="12"
              height="13"
              viewBox="0 0 12 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 12C1.63333 12 1.31944 11.8694 1.05833 11.6083C0.797222 11.3472 0.666667 11.0333 0.666667 10.6667V2H0.65C0.461111 2 0.305556 1.93611 0.183333 1.80833C0.0611111 1.68056 0 1.52222 0 1.33333C0 1.14444 0.0638889 0.986111 0.191667 0.858333C0.319444 0.730556 0.477778 0.666667 0.666667 0.666667H3.33333C3.33333 0.477778 3.39722 0.319444 3.525 0.191667C3.65278 0.0638889 3.81111 0 4 0H6.66667C6.85556 0 7.01389 0.0638889 7.14167 0.191667C7.26945 0.319444 7.33333 0.477778 7.33333 0.666667H10C10.1889 0.666667 10.3472 0.730556 10.475 0.858333C10.6028 0.986111 10.6667 1.14444 10.6667 1.33333C10.6667 1.52222 10.6028 1.68056 10.475 1.80833C10.3472 1.93611 10.1889 2 10 2V4.33333C10 4.52222 9.93611 4.68056 9.80833 4.80833C9.68056 4.93611 9.52222 5 9.33333 5C9.14444 5 8.98611 4.93611 8.85833 4.80833C8.73056 4.68056 8.66667 4.52222 8.66667 4.33333V2H2V10.6667H3.78333C3.97222 10.6667 4.13056 10.7306 4.25833 10.8583C4.38611 10.9861 4.45 11.1444 4.45 11.3333C4.45 11.5111 4.38611 11.6667 4.25833 11.8C4.13056 11.9333 3.97222 12 3.78333 12H2ZM4 3.33333C3.81111 3.33333 3.65278 3.39722 3.525 3.525C3.39722 3.65278 3.33333 3.81111 3.33333 4V8.66667C3.33333 8.85556 3.39722 9.01389 3.525 9.14167C3.65278 9.26945 3.81111 9.33333 4 9.33333C4.18889 9.33333 4.34722 9.26945 4.475 9.14167C4.60278 9.01389 4.66667 8.85556 4.66667 8.66667V4C4.66667 3.81111 4.60278 3.65278 4.475 3.525C4.34722 3.39722 4.18889 3.33333 4 3.33333ZM6.66667 3.33333C6.47778 3.33333 6.31944 3.39722 6.19167 3.525C6.06389 3.65278 6 3.81111 6 4V5C6 5.18889 6.06389 5.34722 6.19167 5.475C6.31944 5.60278 6.47778 5.66667 6.66667 5.66667C6.85556 5.66667 7.01389 5.60278 7.14167 5.475C7.26945 5.34722 7.33333 5.18889 7.33333 5V4C7.33333 3.81111 7.26945 3.65278 7.14167 3.525C7.01389 3.39722 6.85556 3.33333 6.66667 3.33333ZM8.66667 12.6667C7.74444 12.6667 6.95833 12.3417 6.30833 11.6917C5.65833 11.0417 5.33333 10.2556 5.33333 9.33333C5.33333 8.41111 5.65833 7.625 6.30833 6.975C6.95833 6.325 7.74444 6 8.66667 6C9.12222 6 9.55278 6.08889 9.95833 6.26667C10.3639 6.44445 10.7167 6.68333 11.0167 6.98333C11.3167 7.28333 11.5556 7.63611 11.7333 8.04167C11.9111 8.44722 12 8.87778 12 9.33333C12 10.2556 11.675 11.0417 11.025 11.6917C10.375 12.3417 9.58889 12.6667 8.66667 12.6667ZM9 9.2V7.66667C9 7.57778 8.96667 7.5 8.9 7.43333C8.83333 7.36667 8.75556 7.33333 8.66667 7.33333C8.57778 7.33333 8.5 7.36667 8.43333 7.43333C8.36667 7.5 8.33333 7.57778 8.33333 7.66667V9.18333C8.33333 9.27222 8.35 9.35833 8.38333 9.44167C8.41667 9.525 8.46667 9.6 8.53333 9.66667L9.53333 10.6667C9.6 10.7333 9.67778 10.7667 9.76667 10.7667C9.85556 10.7667 9.93333 10.7333 10 10.6667C10.0667 10.6 10.1 10.5222 10.1 10.4333C10.1 10.3444 10.0667 10.2667 10 10.2L9 9.2Z"
                fill="#222222"
              />
            </svg>
            <span className="text-sm text-gray-700">
              {data?.limitFillingLevel
                ? `${data.limitFillingLevel}% ${t("reached at")}`
                : t("Filling time")}
            </span>
          </div>
          <span
            className={`text-sm font-bold ${dataContainer?.durationFullFillingMs?.[0]?.duration === -1 ? "text-red-600" : "text-gray-900"}`}
          >
            {(() => {
              const durationData = dataContainer?.durationFullFillingMs?.[0];
              const reliablePredictionValue =
                extractReliablePrediction(dataContainer);
              // fromDate comes from the backend (historyFillingSpeed endpoint) and represents
              // the start date of the history used to calculate filling speed.
              // The backend may limit this history to a few months even if the container
              // has existed for longer.
              const historyDays = calculateHistoryDays(
                dataContainer?.fromDate,
                currentDate,
              );
              // Check if available history is sufficient (>= 30 days)
              // Returns false if historyDays is null (invalid date or future date)
              const hasEnoughHistory =
                historyDays !== null &&
                historyDays >= MIN_HISTORY_DAYS_FOR_PREDICTION;

              if (durationData?.duration === -1) {
                return t("Limit exceeded");
              }
              if (
                reliablePredictionValue === true &&
                hasOpeningHours(data?.addressJoin?.opening)
              ) {
                if (!hasEnoughHistory) {
                  return t("Not enough history for prediction");
                }
                if (durationData?.predictionDatetime) {
                  const timezone =
                    durationData?.timezone ||
                    data?.addressJoin?.timezone ||
                    "Europe/Paris";
                  const formatted = formattedPredictionDateTime(
                    durationData.predictionDatetime,
                    timezone,
                    t,
                  );
                  if (formatted) {
                    return formatted;
                  }
                }
                if (
                  durationData?.duration &&
                  Number.isFinite(durationData.duration)
                ) {
                  return formattedDuration(durationData.duration);
                }
              }
              return "...";
            })()}
          </span>
        </div>

        {/* Tonnes */}
        <div
          className={`flex items-center justify-between ${data.qualicheck || data.characterization || weightKG === undefined ? "opacity-50" : ""}`}
        >
          <div className="flex items-center gap-2">
            <WeightTonsIcon />
            <span className="text-sm text-gray-700">{t("Tons")}</span>
          </div>
          <span className="text-sm font-bold text-gray-900">
            {!data.qualicheck &&
            !data.characterization &&
            weightKG !== undefined
              ? `${(weightKG / 1000).toFixed(1)}t`
              : "..."}
          </span>
        </div>

        {/* Compactage - N'afficher que si packmatActive */}
        {data.packmatActive && (
          <div
            className={`flex items-center justify-between ${isLoadingHistoryCycleContainer || numberOfPackmat === undefined ? "opacity-50" : ""}`}
          >
            <div className="flex items-center gap-2">
              <CompactionIcon />
              <span className="text-sm text-gray-700">{t("Compaction")}</span>
            </div>
            <span className="text-sm font-bold text-gray-900">
              {!isLoadingHistoryCycleContainer && numberOfPackmat !== undefined
                ? numberOfPackmat
                : "..."}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContainerThumbnailView;
